import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { StageStatusBadge, formatDatePt } from '@/components/Badges';
import { useStageDetail } from './hooks/useStageDetail';
import { StageActionsCard } from './components/StageActionsCard';
import { StageResultCard } from './components/StageResultCard';
import { TiebreakerDialog } from './components/TiebreakerDialog';

function KpiCard({ label, value }) {
  return (
    <Card className="border border-border bg-card">
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="font-display text-3xl text-white mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}

function StageHeader({ stage }) {
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <StageStatusBadge status={stage.status} />
        <span className="text-sm text-muted-foreground">{formatDatePt(stage.date)} • {stage.start_time}</span>
        <span className="text-xs uppercase tracking-widest text-[var(--g66-amber)] border border-[color:hsla(42,92%,52%,0.4)] rounded-full px-2 py-0.5">
          {stage.bands_count === 3 ? '3 bandas' : '2 bandas'}
        </span>
      </div>
      <div className="font-display text-4xl text-white" data-testid="admin-stage-title">{stage.title}</div>
      <div className="text-muted-foreground">
        {stage.band_a?.name || 'A definir'}
        {stage.bands_count === 3 ? (
          <>
            {' '}<span className="text-[var(--g66-red)]">·</span> {stage.band_b?.name || 'A definir'}
            {' '}<span className="text-[var(--g66-red)]">·</span> {stage.band_c?.name || 'A definir'}
          </>
        ) : (
          <>
            {' '}<span className="text-[var(--g66-red)]">vs</span> {stage.band_b?.name || 'A definir'}
          </>
        )}
      </div>
    </>
  );
}

function actionMessage(action, stageStatus) {
  if (action === 'open') return 'Tem certeza que deseja ABRIR a votação? Comandas já poderão ser usadas.';
  if (action === 'close') return 'Tem certeza que deseja FECHAR a votação? Novos votos não serão aceitos.';
  if (action === 'tabulate') return 'Apurar votos irá contar os votos e definir a vencedora. Continuar?';
  if (action === 'delete') {
    return stageStatus === 'finished'
      ? 'Excluir esta etapa finalizada irá remover permanentemente a etapa, suas comandas e todos os votos registrados. O resultado deixará de aparecer no site público. Esta ação NÃO pode ser desfeita. Continuar?'
      : 'Excluir esta etapa irá remover permanentemente a etapa, suas comandas e votos. Esta ação NÃO pode ser desfeita. Continuar?';
  }
  return '';
}

export default function AdminStageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    stage, action, busy, tieOpen, tieForm,
    setAction, setTieOpen, setTieForm,
    perform, submitTie,
  } = useStageDetail(id, navigate);

  if (!stage) return <div className="text-muted-foreground">Carregando…</div>;

  return (
    <div className="space-y-4">
      <Link to="/admin/etapas" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-white">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>

      <StageHeader stage={stage} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KpiCard label="Comandas geradas" value={stage.commands_total} />
        <KpiCard label="Comandas usadas" value={stage.commands_used} />
        <KpiCard label="Total de votos" value={stage.total_votes || 0} />
      </div>

      <StageActionsCard stage={stage} id={id} onRequestAction={setAction} />

      {stage.status === 'finished' && <StageResultCard stage={stage} />}

      {/* Confirm action */}
      <AlertDialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar ação</AlertDialogTitle>
            <AlertDialogDescription>{actionMessage(action, stage?.status)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="action-confirm-button"
              onClick={perform}
              disabled={busy}
              className={action === 'delete'
                ? 'bg-[var(--g66-red)] text-white hover:bg-[var(--g66-red-2)]'
                : 'bg-[var(--g66-amber)] text-black hover:bg-[var(--g66-amber-2)]'
              }
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (action === 'delete' ? 'Excluir definitivamente' : 'Confirmar')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TiebreakerDialog
        open={tieOpen}
        stage={stage}
        tieForm={tieForm}
        setTieForm={setTieForm}
        onSubmit={submitTie}
        onOpenChange={setTieOpen}
        busy={busy}
      />
    </div>
  );
}
